import { useEffect, useState } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

function Documents() {
  const { hasPermission, user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const canApprove = hasPermission('documents', 'update');
  const canDelete = hasPermission('documents', 'delete');

  useEffect(() => {
    fetchDocuments();
    fetchEmployees();
  }, []);

  useEffect(() => {
    filterDocuments();
  }, [documents, statusFilter, searchTerm, employeeFilter]);

  const fetchDocuments = async () => {
    try {
      const response = await api.get('/documents');
      setDocuments(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees');
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const filterDocuments = () => {
    let filtered = [...documents];

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(doc => doc.status === statusFilter);
    }

    // Filter by employee (for HR/Admin)
    if (employeeFilter !== 'all') {
      filtered = filtered.filter(doc => doc.uploadedBy?._id === employeeFilter);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(doc =>
        doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredDocuments(filtered);
  };

  const handleStatusUpdate = async (id, status, rejectionReason = '') => {
    try {
      await api.put(`/documents/${id}/status`, { 
        status,
        rejectionReason 
      });
      fetchDocuments();
      alert(`Document ${status} successfully!`);
    } catch (error) {
      alert(`Error updating document status`);
      console.error('Status update error:', error);
    }
  };

  const handleApprove = (id) => {
    if (window.confirm('Are you sure you want to approve this document?')) {
      handleStatusUpdate(id, 'approved');
    }
  };

  const handleReject = (id) => {
    const reason = prompt('Please provide a reason for rejection:');
    if (reason) {
      handleStatusUpdate(id, 'rejected', reason);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await api.delete(`/documents/${id}`);
        fetchDocuments();
        alert('Document deleted successfully!');
      } catch (error) {
        alert('Error deleting document');
        console.error('Delete error:', error);
      }
    }
  };

  const handleDownload = async (id, filename) => {
    try {
      const response = await api.get(`/documents/${id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Error downloading document');
      console.error('Download error:', error);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge-warning',
      approved: 'badge-success',
      rejected: 'badge-danger'
    };
    return badges[status] || 'badge-info';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Aadhar Card': '🆔',
      'PAN Card': '💳',
      'Bank Account': '🏦',
      'Passport': '🛂',
      'Driving License': '🚗',
      'Marksheet': '📜',
      'Degree Certificate': '🎓',
      'Experience Letter': '📋',
      'Offer Letter': '💼',
      'Appointment Letter': '📑',
      'Relieving Letter': '📄',
      'Policy': '📋',
      'Contract': '📝',
      'Report': '📊',
      'Other': '📄'
    };
    return icons[category] || '📄';
  };

  if (loading) {
    return <div className="container">Loading...</div>;
  }

  // Calculate statistics
  const totalDocuments = documents.length;
  const pendingApproval = documents.filter(doc => doc.status === 'pending').length;
  const approvedDocuments = documents.filter(doc => doc.status === 'approved').length;
  const rejectedDocuments = documents.filter(doc => doc.status === 'rejected').length;

  return (
    <div className="container">
      <div className="flex-between mb-20" style={{ flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 'clamp(20px, 5vw, 28px)', margin: 0 }}>📁 Document Management</h1>
      </div>

      {/* Statistics Cards */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))',
        gap: '15px', 
        marginBottom: '20px'
      }}>
        <div className="card" style={{
          minHeight: '90px',
          padding: '15px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          border: 'none'
        }}>
          <div style={{ fontSize: 'clamp(22px, 5vw, 28px)', fontWeight: 'bold', marginBottom: '5px' }}>
            {totalDocuments}
          </div>
          <div style={{ fontSize: 'clamp(11px, 3vw, 14px)', opacity: 0.9 }}>
            📄 Total Documents
          </div>
        </div>

        <div className="card" style={{
          minHeight: '90px',
          padding: '15px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          color: 'white',
          border: 'none'
        }}>
          <div style={{ fontSize: 'clamp(22px, 5vw, 28px)', fontWeight: 'bold', marginBottom: '5px' }}>
            {pendingApproval}
          </div>
          <div style={{ fontSize: 'clamp(11px, 3vw, 14px)', opacity: 0.9 }}>
            ⏳ Pending Approval
          </div>
        </div>

        <div className="card" style={{
          minHeight: '90px',
          padding: '15px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          color: 'white',
          border: 'none'
        }}>
          <div style={{ fontSize: 'clamp(22px, 5vw, 28px)', fontWeight: 'bold', marginBottom: '5px' }}>
            {approvedDocuments}
          </div>
          <div style={{ fontSize: 'clamp(11px, 3vw, 14px)', opacity: 0.9 }}>
            ✅ Approved
          </div>
        </div>

        <div className="card" style={{
          minHeight: '90px',
          padding: '15px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
          color: 'white',
          border: 'none'
        }}>
          <div style={{ fontSize: 'clamp(22px, 5vw, 28px)', fontWeight: 'bold', marginBottom: '5px' }}>
            {rejectedDocuments}
          </div>
          <div style={{ fontSize: 'clamp(11px, 3vw, 14px)', opacity: 0.9 }}>
            ❌ Rejected
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '15px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
              Search Documents
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="Search by title, description, or category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
              Filter by Status
            </label>
            <select
              className="form-control"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Documents</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
              Filter by Employee
            </label>
            <select
              className="form-control"
              value={employeeFilter}
              onChange={(e) => setEmployeeFilter(e.target.value)}
            >
              <option value="all">All Employees</option>
              {employees.map(emp => (
                <option key={emp._id} value={emp._id}>
                  {emp.firstName} {emp.lastName} ({emp.employeeId})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Documents Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
        gap: '20px' 
      }}>
        {filteredDocuments.length === 0 ? (
          <div className="card" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px' }}>
            <p style={{ color: '#666', fontSize: '16px' }}>No documents found</p>
          </div>
        ) : (
          filteredDocuments.map((doc) => (
            <div key={doc._id} className="card" style={{ 
              display: 'flex', 
              flexDirection: 'column',
              height: '100%'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '10px',
                marginBottom: '15px',
                paddingBottom: '15px',
                borderBottom: '1px solid #e5e7eb'
              }}>
                <span style={{ fontSize: '32px' }}>{getCategoryIcon(doc.category)}</span>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: '0 0 5px 0', fontSize: '16px', fontWeight: '600' }}>
                    {doc.title}
                  </h3>
                  <span className={`badge ${getStatusBadge(doc.status)}`}>
                    {doc.status}
                  </span>
                </div>
              </div>

              <div style={{ flex: 1 }}>
                <p style={{ 
                  fontSize: '13px', 
                  color: '#666',
                  marginBottom: '10px',
                  lineHeight: '1.5'
                }}>
                  {doc.description || 'No description provided'}
                </p>

                <div style={{ fontSize: '12px', color: '#999', marginBottom: '15px' }}>
                  <p style={{ margin: '5px 0' }}>
                    <strong>Category:</strong> {doc.category}
                  </p>
                  <p style={{ margin: '5px 0' }}>
                    <strong>Uploaded by:</strong> {doc.uploadedBy?.firstName} {doc.uploadedBy?.lastName}
                  </p>
                  <p style={{ margin: '5px 0' }}>
                    <strong>Date:</strong> {new Date(doc.createdAt).toLocaleDateString()}
                  </p>
                  {doc.rejectionReason && (
                    <p style={{ margin: '5px 0', color: '#dc2626' }}>
                      <strong>Rejection Reason:</strong> {doc.rejectionReason}
                    </p>
                  )}
                </div>
              </div>

              <div style={{ 
                display: 'flex', 
                gap: '8px', 
                paddingTop: '15px',
                borderTop: '1px solid #e5e7eb'
              }}>
                <button 
                  className="btn btn-secondary"
                  onClick={() => handleDownload(doc._id, doc.filename)}
                  style={{ flex: 1, fontSize: '12px', padding: '8px' }}
                >
                  📥 Download
                </button>
                
                {canApprove && doc.status === 'pending' && (
                  <>
                    <button 
                      className="btn btn-primary"
                      onClick={() => handleApprove(doc._id)}
                      style={{ flex: 1, fontSize: '12px', padding: '8px' }}
                    >
                      ✓ Approve
                    </button>
                    <button 
                      className="btn"
                      onClick={() => handleReject(doc._id)}
                      style={{ 
                        flex: 1, 
                        fontSize: '12px', 
                        padding: '8px',
                        background: '#dc2626',
                        color: 'white'
                      }}
                    >
                      ✕ Reject
                    </button>
                  </>
                )}
                
                {canDelete && (
                  <button 
                    className="btn"
                    onClick={() => handleDelete(doc._id)}
                    style={{ 
                      fontSize: '12px', 
                      padding: '8px',
                      background: '#666',
                      color: 'white'
                    }}
                  >
                    🗑️
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default Documents;
